import React from "react"
import { Checkbox, Grid } from "@material-ui/core"

import { Text, themeColors } from "letalkui"

import { PermissionEffect, PermissionFeature, PermissionFeatureToActions, Permissions } from "@/protocols/permission"
import { getPermissionTranslated } from "@/pages/Admin/Role/RoleDetails/permissionLabels"
import { PermissionSubgroupConfig } from "@/pages/Admin/Role/RoleDetails/permissionGroupsConfig"

import useStyles from "@/pages/Admin/Role/RoleDetails/PermissionSubgroup/styles"
import RoleGroupPermissionGate from "@/components/PermissionGate/RoleGroupPermissionGate"

type PermissionSubgroupProps = PermissionSubgroupConfig & {
	permissions: Permissions
	onChangePermission: (permissions: Permissions) => void
}

const PermissionSubgroup: React.FC<PermissionSubgroupProps> = (props) => {
	const {
		label,
		feature,
		actions,
		permissions,
		onChangePermission
	} = props

	const classes = useStyles()

	const storedActions = permissions[feature] ?? []

	const items = actions.map(action => {
		const storedAction = storedActions.find(permission => permission.action === action)

		return storedAction ?? { action, effect: "deny" as PermissionEffect }
	})

	const checkedCount = items.filter(item => item.effect === "allow").length
	const totalCount = items.length
	const isSubgroupChecked = totalCount > 0 && checkedCount === totalCount

	const buildPermissionsWithAdminResetIfDenied = (updatedFeaturePermissions: Permissions, checked: boolean): Permissions => {
		if (checked) {
			return updatedFeaturePermissions
		}

		return {
			...updatedFeaturePermissions,
			system: (updatedFeaturePermissions.system ?? []).map(permission => permission.action === "admin" ? { ...permission, effect: "deny" as PermissionEffect } : permission)
		}
	}

	const upsertPermissionActions = (
		current: typeof storedActions,
		targetActions: PermissionFeatureToActions[PermissionFeature][],
		effect: PermissionEffect
	) => {
		const updated = current.map(permission => (
			(targetActions as string[]).includes(permission.action) ? { ...permission, effect } : permission
		))

		const missingActions = targetActions.filter(action => !current.some(permission => permission.action === action))

		return [...updated, ...missingActions.map(action => ({ action, effect }))]
	}

	const handleChangeItem = (action: PermissionFeatureToActions[PermissionFeature], checked: boolean) => {
		const effect: PermissionEffect = checked ? "allow" : "deny"

		const updatedPermissions: Permissions = buildPermissionsWithAdminResetIfDenied({
			...permissions,
			[feature]: upsertPermissionActions(storedActions, [action], effect)
		}, checked)

		onChangePermission(updatedPermissions)
	}

	const handleChangeSubgroupSelectAll = (checked: boolean) => {
		const effect: PermissionEffect = checked ? "allow" : "deny"

		const updatedPermissions: Permissions = buildPermissionsWithAdminResetIfDenied({
			...permissions,
			[feature]: upsertPermissionActions(storedActions, actions, effect)
		}, checked)

		onChangePermission(updatedPermissions)
	}

	return (
		<Grid container>
			<Grid item xs={12}>
				<Grid container wrap="nowrap" justifyContent="space-between" alignItems="center" className={classes.subgroupHeader}>
					<Grid item>
						<Text.Body type="small2" fontWeight="bold" sx={classes.subgroupLabel}>{label}</Text.Body>
					</Grid>

					<Grid item>
						<Grid container alignItems="center" spacing={1.5}>
							<Grid item>
								<span className={classes.subgroupCount}>{checkedCount}/{totalCount}</span>
							</Grid>
							<Grid item>
								<RoleGroupPermissionGate action="update">
									<Checkbox
										color="primary"
										checked={isSubgroupChecked}
										onChange={(_, checked) => handleChangeSubgroupSelectAll(checked)}
									/>
								</RoleGroupPermissionGate>
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			</Grid>

			<Grid item xs={12} className={classes.itemsContainer}>
				{items.map((item, index) => {
					const permissionTranslatedData = getPermissionTranslated(feature, item.action)
					const isLastItem = index === items.length - 1

					return (
						<Grid
							container
							key={item.action}
							justifyContent="space-between"
							alignItems="flex-start"
							className={`${classes.itemRow} ${!isLastItem ? classes.itemBorder : ""}`}
						>
							<Grid item xs={10}>
								<Grid container direction="column" spacing={0.5}>
									<Grid item>
										<Text.Body type="small1" fontWeight="bold" sx={classes.itemLabel}>{permissionTranslatedData.label}</Text.Body>
									</Grid>
									<Grid item>
										<Text.Body type="small2" fontWeight="regular" color={themeColors.colors.system.gray[500]} className={classes.itemDescription}>{permissionTranslatedData.description}</Text.Body>
									</Grid>
								</Grid>
							</Grid>
							<Grid item>
								<RoleGroupPermissionGate action="update">
									<Checkbox
										color="primary"
										checked={item.effect === "allow"}
										onChange={(_, checked) => handleChangeItem(item.action, checked)}
									/>
								</RoleGroupPermissionGate>
							</Grid>
						</Grid>
					)
				})}
			</Grid>
		</Grid>
	)
}

export default PermissionSubgroup
